// ── PeerComparePreview — 동네/업종 셈핏 매장 비교 (V79)
// 4종 비교 카드: 공급처 리뷰, 메뉴 마진율, 알바 시급, 공동구매
// 0 user 단계 = empty state로 가치 약속 + 진척바 (data → data 보상 회로)
//
// 데이터 집계는 추후 Cloud Function으로 익명 합산 후 카드에 표시 예정.
// 지금은 UI 셸 + "데이터 N개 더 모이면 unlock" 메시지로 가치 노출.

import React, { useMemo } from 'react';

const fmtWon = n => Math.round(Number(n) || 0).toLocaleString('ko-KR') + '원';

// ── 카드 1: 공급처 리뷰/가격 ──
// 영수증 OCR / 거래장부 lineItems에서 추출한 거래처 데이터.
// 익명 집계 → 평점·가격 동네 평균 노출.
function SupplierComparisonCard({ transactions, isMobile, onNavigate }) {
  const data = useMemo(() => {
    const map = {};
    (transactions || []).forEach(t => {
      const p = (t.partnerName || '').trim();
      if (!p) return;
      if (!map[p]) map[p] = { count: 0 };
      map[p].count += 1;
    });
    const arr = Object.entries(map)
      .map(([name, v]) => ({ name, ...v }))
      .sort((a, b) => b.count - a.count);
    return arr;
  }, [transactions]);

  const myCount = data.length;

  return (
    <Card>
      <Label>공급처 평가</Label>
      <Body>
        {myCount === 0 ? (
          <Empty>거래처를 입력하면 동네 사장님들의 평가가 보입니다</Empty>
        ) : (
          <>
            <ExampleLine
              bold={`내 거래처 ${myCount}곳`}
              sub="익명 평점·가격 비교 곧 공개"
            />
            <ProgressNote
              cur={1}
              target={5}
              text="동네 5매장이 같은 거래처를 등록하면 평균 평점 공개"
            />
          </>
        )}
      </Body>
      <Footer onClick={() => onNavigate?.('purchase')}>거래처 보기 →</Footer>
    </Card>
  );
}

// ── 카드 2: 메뉴 마진율 ──
function MenuMarginCard({ categories, prices, onNavigate }) {
  const menuCount = useMemo(() => {
    if (!Array.isArray(categories)) return 0;
    let n = 0;
    categories.forEach(c => {
      if (Array.isArray(c.items)) n += c.items.length;
    });
    return n;
  }, [categories]);

  return (
    <Card>
      <Label>메뉴 마진율 비교</Label>
      <Body>
        {menuCount === 0 ? (
          <Empty>레시피를 등록하면 같은 메뉴 매장과 비교됩니다</Empty>
        ) : (
          <>
            <ExampleLine
              bold={`내 메뉴 ${menuCount}개`}
              sub="같은 메뉴 14매장이 모이면 마진율 분포 공개"
            />
            <ProgressNote
              cur={0}
              target={14}
              text="같은 업종 매장이 같은 메뉴를 등록하면 자동 매칭"
            />
          </>
        )}
      </Body>
      <Footer onClick={() => onNavigate?.('recipes')}>레시피 보기 →</Footer>
    </Card>
  );
}

// ── 카드 3: 알바 시급 ──
function HourlyWageCard({ payrollSettings, employees, onNavigate }) {
  const myWage = useMemo(() => {
    const list = (employees || []).filter(e => e.active !== false && Number(e.hourlyWage) > 0);
    if (list.length === 0) return null;
    const avg = list.reduce((s, e) => s + Number(e.hourlyWage), 0) / list.length;
    return Math.round(avg);
  }, [employees]);

  return (
    <Card>
      <Label>동네 알바 시급</Label>
      <Body>
        {myWage === null ? (
          <Empty>직원을 등록하면 동네 시급 평균과 비교됩니다</Empty>
        ) : (
          <>
            <ExampleLine
              bold={`내 매장 평균 시급 ${myWage.toLocaleString()}원`}
              sub="동네 외식업 5매장 모이면 시세 공개"
            />
            <ProgressNote
              cur={1}
              target={5}
              text="같은 지역 매장이 인건비를 등록하면 자동 집계"
            />
          </>
        )}
      </Body>
      <Footer onClick={() => onNavigate?.('payroll')}>인건비 보기 →</Footer>
    </Card>
  );
}

// ── 카드 4: 공동구매 안건 ──
function GroupBuyHintCard({ onNavigate }) {
  return (
    <Card>
      <Label>공동구매 안건</Label>
      <Body>
        <ExampleLine
          bold="진행 중 0건"
          sub="같은 거래처·같은 품목 → 자동 추천"
        />
        <ProgressNote
          cur={0}
          target={4}
          text="같은 동네 사장님 4명이 같은 품목을 발주하면 안건 발의"
        />
      </Body>
      <Footer onClick={() => onNavigate?.('groupBuy')}>공동구매 보기 →</Footer>
    </Card>
  );
}

// ── 메인 컴포넌트 ──
export default function PeerComparePreview({
  transactions, categories, prices, employees, payrollSettings,
  isMobile, onNavigate,
}) {
  return (
    <div style={{
      background: '#fff', borderRadius: 16,
      padding: isMobile ? '16px 14px' : '20px 22px',
      marginBottom: 18,
      boxShadow: '0 3px 18px rgba(0,0,0,0.08)',
    }}>
      {/* 헤더 */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#18181B' }}>
          동네·업종 셈핏 매장 비교
        </div>
        <div style={{ fontSize: 11, color: '#71717A', marginTop: 3 }}>
          데이터가 모일수록 더 정밀한 비교가 자동으로 활성화됩니다
        </div>
      </div>

      {/* 4종 카드 그리드 */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: 10,
      }}>
        <SupplierComparisonCard
          transactions={transactions}
          isMobile={isMobile}
          onNavigate={onNavigate}
        />
        <MenuMarginCard
          categories={categories}
          prices={prices}
          onNavigate={onNavigate}
        />
        <HourlyWageCard
          payrollSettings={payrollSettings}
          employees={employees}
          onNavigate={onNavigate}
        />
        <GroupBuyHintCard onNavigate={onNavigate} />
      </div>

      {/* 하단 안내 */}
      <div style={{
        marginTop: 12, padding: '8px 12px',
        background: '#FAFAFA', borderRadius: 8,
        fontSize: 11, color: '#52525B', lineHeight: 1.55,
      }}>
        모든 비교는 익명으로 집계되며, 다른 매장에 내 매출/상호는 노출되지 않습니다.
      </div>
    </div>
  );
}

// ── 작은 building blocks ──
function Card({ children }) {
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #F4F4F5',
      borderRadius: 10,
      padding: '12px 14px',
      display: 'flex', flexDirection: 'column',
      gap: 8,
    }}>
      {children}
    </div>
  );
}

function Label({ children }) {
  return (
    <div style={{
      fontSize: 12, fontWeight: 800, color: '#18181B',
      letterSpacing: '-0.01em',
    }}>{children}</div>
  );
}

function Body({ children }) {
  return <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>{children}</div>;
}

function Empty({ children }) {
  return (
    <div style={{
      fontSize: 12, color: '#71717A', lineHeight: 1.55,
      padding: '4px 0',
    }}>{children}</div>
  );
}

function ExampleLine({ bold, sub }) {
  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#18181B' }}>{bold}</div>
      {sub && (
        <div style={{ fontSize: 11, color: '#71717A', marginTop: 2 }}>{sub}</div>
      )}
    </div>
  );
}

function ProgressNote({ cur, target, text }) {
  return (
    <div style={{
      fontSize: 10, color: '#71717A',
      padding: '6px 8px',
      background: '#FAFAFA', borderRadius: 6,
      lineHeight: 1.5,
    }}>
      <div style={{ fontWeight: 700, color: '#52525B', marginBottom: 2 }}>
        진행도 {cur} / {target}
      </div>
      {text}
    </div>
  );
}

function Footer({ children, onClick }) {
  return (
    <button onClick={onClick} style={{
      background: 'transparent', border: 'none',
      color: '#52525B', fontSize: 11, fontWeight: 700,
      textAlign: 'left', cursor: 'pointer', padding: 0,
      marginTop: 2,
    }}>{children}</button>
  );
}
